/**
 * Ten random tickets that were over the p90 they were counted in, with links,
 * so the P90 charts can be spot-checked against real tickets.
 *
 * "Over the p90" is per brand per solve day, because that is how the stored
 * p90s are computed — see backend services/zendeskTailSample.
 */

import { format } from 'date-fns'
import { useState, useEffect, useCallback } from 'react'
import { zendeskAPI } from '../services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from './ui'

const METRICS = [
  { key: 'first_reply', label: 'First reply' },
  { key: 'solve', label: 'Solve' },
]

function formatMinutes(mins) {
  if (mins == null) return '--'
  if (mins < 60) return `${Math.round(mins)}m`
  if (mins < 1440) return `${(mins / 60).toFixed(1)}h`
  return `${(mins / 1440).toFixed(1)}d`
}

// Day labels are stored as UTC midnight, so read them in UTC or they shift a day.
function formatDay(value) {
  if (!value) return '--'
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export default function P90TailSample({ dateRange, aggregate = false, scopeKey }) {
  const [metric, setMetric] = useState('first_reply')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const startDate = format(dateRange?.from, 'yyyy-MM-dd')
  const endDate = format(dateRange?.to, 'yyyy-MM-dd')

  const load = useCallback(async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    setError(null)
    try {
      const params = { metric, startDate, endDate, size: 10 }
      if (aggregate) params.aggregate = 1
      const res = await zendeskAPI.getTailSample(params)
      setResult(res.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sample')
    } finally {
      setLoading(false)
    }
    // scopeKey reloads the sample when the selected clinic changes.
  }, [metric, startDate, endDate, aggregate, scopeKey])

  useEffect(() => { load() }, [load])

  const tickets = result?.tickets || []

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle>Tickets over P90</CardTitle>
            <CardDescription>
              A random {tickets.length || 10} of the {result ? result.total_over.toLocaleString() : '…'} tickets
              in this range whose {metric === 'solve' ? 'solve time' : 'first reply'} beat the P90 of their
              brand on the day they were solved — the figure the charts plot.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center rounded-lg border border-border overflow-hidden">
              {METRICS.map(m => (
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-r border-border last:border-r-0 ${
                    metric === m.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted text-muted-foreground'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Reshuffle'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : !loading && result && tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tickets over P90 in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-2 px-2 font-medium">Ticket</th>
                  <th className="text-left py-2 px-2 font-medium">Subject</th>
                  {aggregate && <th className="text-left py-2 px-2 font-medium">Brand</th>}
                  <th className="text-left py-2 px-2 font-medium">Solved</th>
                  <th className="text-right py-2 px-2 font-medium">{metric === 'solve' ? 'Solve time' : 'First reply'}</th>
                  <th className="text-right py-2 px-2 font-medium">Day P90</th>
                </tr>
              </thead>
              <tbody className={loading ? 'opacity-50' : ''}>
                {tickets.map(t => (
                  <tr key={`${t.ticket_id}-${t.data_date}`} className="border-b border-border/50 last:border-0">
                    <td className="py-2 px-2 whitespace-nowrap">
                      <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        #{t.ticket_id}
                      </a>
                    </td>
                    <td className="py-2 px-2 max-w-xs truncate" title={t.subject || ''}>{t.subject || '(no subject)'}</td>
                    {aggregate && <td className="py-2 px-2 whitespace-nowrap">{t.brand_name}</td>}
                    <td className="py-2 px-2 whitespace-nowrap">{formatDay(t.data_date)}</td>
                    <td className="py-2 px-2 text-right whitespace-nowrap font-medium">{formatMinutes(t.value_minutes)}</td>
                    <td className="py-2 px-2 text-right whitespace-nowrap text-muted-foreground">{formatMinutes(t.p90_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
